import { useState, useCallback, useEffect, useRef } from 'react';

export default function usePdfState() {
  const [pdfState, setPdfState] = useState({
    filePath: "",
    pageNumber: 1,
    zoom: 1.0,
    highlightMode: false,
    commentMode: false,
    selectedText: "",
    selectionRects: [],
    highlights: [],
    comments: [],
    numPages: 0
  });

  // Use refs to track previous values and prevent infinite loops
  const prevFilePathRef = useRef("");
  const isResettingRef = useRef(false);

  // Reset state when filePath changes - FIXED VERSION
  useEffect(() => {
    // Skip if no filePath or if it's the default document
    if (!pdfState.filePath || pdfState.filePath === "Untitled Document") {
      return;
    }

    // Skip if filePath hasn't actually changed
    if (pdfState.filePath === prevFilePathRef.current) {
      return;
    }

    // Skip if we're already in the middle of a reset
    if (isResettingRef.current) {
      return;
    }

    console.log('🔄 File changed, resetting PDF state for:', pdfState.filePath);
    
    // Mark that we're resetting
    isResettingRef.current = true;

    setPdfState(prev => {
      // Only reset if the filePath is actually different
      if (prev.filePath === pdfState.filePath) {
        return {
          ...prev,
          pageNumber: 1,
          zoom: 1.0,
          highlightMode: false,
          commentMode: false,
          selectedText: "",
          selectionRects: [],
          highlights: [],
          comments: [],
          numPages: 0
        };
      }
      return prev; // Return unchanged if filePath doesn't match
    });

    // Update the ref
    prevFilePathRef.current = pdfState.filePath;
    
    // Reset the flag after state update
    setTimeout(() => {
      isResettingRef.current = false;
    }, 0);

  }, [pdfState.filePath]);

  const updatePdfState = useCallback((updates) => {
    // Prevent setting the same values
    setPdfState(prev => {
      // Check if any values actually changed
      const hasChanges = Object.keys(updates).some(key => 
        prev[key] !== updates[key]
      );
      
      if (!hasChanges) {
        console.log('⏭️ Skipping state update - no changes');
        return prev;
      }
      
      console.log('🔄 State update:', Object.keys(updates));
      return { ...prev, ...updates };
    });
  }, []);

  const actions = {
    setFilePath: (filePath) => {
      if (filePath !== pdfState.filePath) {
        updatePdfState({ filePath });
      }
    },
    setZoom: (zoom) => {
      if (zoom !== pdfState.zoom) {
        updatePdfState({ zoom });
      }
    },
    setHighlightMode: (highlightMode) => {
      if (highlightMode !== pdfState.highlightMode) {
        updatePdfState({ highlightMode });
      }
    },
    setCommentMode: (commentMode) => {
      if (commentMode !== pdfState.commentMode) {
        updatePdfState({ commentMode });
      }
    },
    setPageNumber: (pageNumber) => {
      if (pageNumber !== pdfState.pageNumber) {
        updatePdfState({ pageNumber });
      }
    },
    setNumPages: (numPages) => {
      if (numPages !== pdfState.numPages) {
        updatePdfState({ numPages });
      }
    },
    setSelectedText: (selectedText) => {
      if (selectedText !== pdfState.selectedText) {
        updatePdfState({ selectedText });
      }
    },
    setSelectionRects: (selectionRects) => {
      // For arrays, check if they're actually different
      const isDifferent = 
        selectionRects.length !== pdfState.selectionRects.length ||
        selectionRects.some((rect, i) => 
          !pdfState.selectionRects[i] ||
          rect.top !== pdfState.selectionRects[i].top ||
          rect.left !== pdfState.selectionRects[i].left
        );
      
      if (isDifferent) {
        updatePdfState({ selectionRects });
      }
    },
    setHighlights: (highlights) => {
      // For arrays, check if they're actually different
      const isDifferent = 
        highlights.length !== pdfState.highlights.length ||
        highlights.some((h, i) => 
          !pdfState.highlights[i] ||
          h.id !== pdfState.highlights[i].id
        );
      
      if (isDifferent) {
        updatePdfState({ highlights });
      }
    },
    setComments: (comments) => {
      // For arrays, check if they're actually different
      const isDifferent = 
        comments.length !== pdfState.comments.length ||
        comments.some((c, i) => 
          !pdfState.comments[i] ||
          c.id !== pdfState.comments[i].id
        );
      
      if (isDifferent) {
        updatePdfState({ comments });
      }
    },
    
    resetState: (newFilePath = "") => {
      console.log('🔄 Manual state reset for new document:', newFilePath);
      
      // Prevent resetting to same state
      if (
        newFilePath === pdfState.filePath &&
        pdfState.pageNumber === 1 &&
        pdfState.zoom === 1.0 &&
        pdfState.highlightMode === false &&
        pdfState.commentMode === false &&
        pdfState.selectedText === "" &&
        pdfState.selectionRects.length === 0 &&
        pdfState.highlights.length === 0 &&
        pdfState.comments.length === 0 &&
        pdfState.numPages === 0
      ) {
        console.log('⏭️ Skipping reset - already in reset state');
        return;
      }
      
      setPdfState({
        filePath: newFilePath,
        pageNumber: 1,
        zoom: 1.0,
        highlightMode: false,
        commentMode: false,
        selectedText: "",
        selectionRects: [],
        highlights: [],
        comments: [],
        numPages: 0
      });
      
      // Update refs
      prevFilePathRef.current = newFilePath;
      isResettingRef.current = false;
    },
    
    clearHighlights: () => {
      if (pdfState.highlights.length > 0) {
        updatePdfState({ highlights: [] });
      }
    },
    
    clearComments: () => {
      if (pdfState.comments.length > 0) {
        updatePdfState({ comments: [] });
      }
    },
    
    clearCurrentPageHighlights: () => {
      const filteredHighlights = pdfState.highlights.filter(h => h.pageNumber !== pdfState.pageNumber);
      if (filteredHighlights.length !== pdfState.highlights.length) {
        updatePdfState({ highlights: filteredHighlights });
      }
    },
    
    clearCurrentPageComments: () => {
      const filteredComments = pdfState.comments.filter(c => c.pageNumber !== pdfState.pageNumber);
      if (filteredComments.length !== pdfState.comments.length) {
        updatePdfState({ comments: filteredComments });
      }
    }
  };

  return { ...pdfState, ...actions };
}